import React from "react";
import { HoverRows } from "./GeotabHover.jsx";
import { THRESHOLD_CHOICES } from "./geotabPreferences.js";
import { planBarCells, planBarMore, planBarStops, weekText } from "./geotabPlanBar.js";

// Barre commune de Dispatch vs Geotab (une journée), du Calibrage et du Tableau de bord (une période) : le respect du plan et les durées
// moyennes d'abord, Chargés PM, préchargements et heures demandées en dessous. Compacte (libellé et chiffre sur une ligne) pour garder
// la carte visible en 1080p.
// Couleurs des écarts de Geotab : rouge = retard ou non fait, violet = en avance.
const SUB_TONE = { late: "font-bold text-[#c5524a]", early: "font-bold text-[#4c2f86]" };

// Petites barres des semaines (dimanche-samedi), la dernière en foncé ; un écart d'heures se lit au-dessus ou au-dessous de zéro.
function WeekBars({ values, signed }) {
  const max = Math.max(...values.map((v) => Math.abs(v ?? 0)), 1);
  const gap = values.length > 8 ? 1 : 2;
  const width = (64 - gap * (values.length - 1)) / values.length;
  return (
    <svg className="h-4 w-10 flex-none self-center" viewBox="0 0 64 26" preserveAspectRatio="none" aria-hidden="true">
      {signed && <line x1="0" x2="64" y1="13" y2="13" stroke="#cbd5e1" strokeWidth="1" />}
      {values.map((v, i) => {
        const h = Math.max(1, ((signed ? 12 : 26) * Math.abs(v ?? 0)) / max);
        return <rect key={i} x={i * (width + gap)} y={signed ? ((v ?? 0) >= 0 ? 13 - h : 13) : 26 - h} width={width} height={h} rx="1"
          fill={i === values.length - 1 ? "#334155" : "#94a3b8"} />;
      })}
    </svg>
  );
}

export default function GeotabPlanBar({ bar, timing, scope = "day", date = null, period = "day", tolerance = 15, onToleranceChange = null, baseline = null, aside = null }) {
  if (!bar) return null;
  const cells = planBarCells(bar, scope, { timing, baseline, tolerance });
  const stops = planBarStops(bar);
  const more = planBarMore(bar);
  const dayLabel = scope === "day" && date
    ? `Journée du ${new Date(`${date}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short", year: "numeric" })} · ${period === "evening" ? "Soir" : "Jour"}` : null;
  return (
    <section className="overflow-hidden rounded-[4px] border border-slate-200 bg-white" aria-label="Respect du plan" data-geotab-plan-bar={scope}
      data-dispatch-date={dayLabel ? date : undefined} data-dispatch-period={dayLabel ? period : undefined}>
      <div className="grid grid-cols-8 max-[1500px]:grid-cols-4 max-[700px]:grid-cols-2">
        {cells.map((cell, i) => (
          <HoverRows key={cell.key} title={cell.label} rows={[...cell.hover,
            ...(cell.key === "stops" ? stops.map((item) => [item.label, `${item.value} · ${item.count || item.missing}${item.count ? ` · ${item.missing}` : ""}`]) : []),
            cell.weeks ? ["Par semaine", weekText(cell.weeks, cell.unit)] : null]}>
            <div className={`flex min-w-0 cursor-help items-center gap-x-3 px-3 py-1 ${cell.key === "stops" ? "col-span-2" : ""} ${i ? "shadow-[inset_1px_0_0_#eef2f5]" : ""}`} data-plan-bar-cell={cell.key}>
              <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2">
                <span className="min-w-0 text-[11.5px] font-bold leading-snug text-slate-700">{cell.label}</span>
                <span className="whitespace-nowrap text-[17px] font-bold leading-tight tracking-[-0.01em] text-slate-900 tabular-nums">{cell.value}</span>
                <span className={`min-w-0 text-[11px] leading-snug text-slate-600 ${cell.weeks ? "" : "col-span-2"}`}>
                  {cell.sub.map((part, j) => (
                    <React.Fragment key={part.text}>{j ? " · " : ""}<span className={SUB_TONE[part.tone] || ""}>{part.text}</span></React.Fragment>
                  ))}
                </span>
                {cell.weeks && <WeekBars values={cell.weeks} signed={cell.unit === "delta"} />}
              </div>
              {cell.key === "stops" && (
                <div className="grid flex-none gap-y-0.5 border-l border-slate-200 pl-3 text-[11px] leading-snug text-slate-600" role="group" aria-label="Détail des arrêts">
                  {stops.map((item) => (
                    <span key={item.key} className="flex items-baseline justify-between gap-x-2 whitespace-nowrap" data-plan-bar-detail={item.key}>
                      {item.label} <b className="text-slate-900 tabular-nums">{item.value}</b>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </HoverRows>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-y-1 border-t border-slate-200 bg-slate-50 px-3 py-1 text-[12px] text-slate-600">
        <b className="mr-3 text-[10.5px] font-bold uppercase tracking-[0.06em] text-slate-700">{dayLabel || "Autres mesures"}</b>
        {more.map((item, i) => {
          const content = (
            <span className={`inline-flex items-center gap-1.5 whitespace-nowrap ${i ? "border-l border-slate-200 px-3" : "pr-3"} ${item.hover ? "cursor-help" : ""}`} data-plan-bar-more={item.key}>
              {item.label} <b className="text-[12.5px] text-slate-900 tabular-nums">{item.value}</b>
              {item.sub.map((part) => <span key={part.text} className={`text-[11px] ${SUB_TONE[part.tone] || ""}`}>{part.text}</span>)}
            </span>
          );
          return item.hover
            ? <HoverRows key={item.key} title={item.label} rows={item.hover}>{content}</HoverRows>
            : <React.Fragment key={item.key}>{content}</React.Fragment>;
        })}
        {(onToleranceChange || aside) && (
          <span className="ml-auto inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1">
            {aside}
            {onToleranceChange && (
              <label className="inline-flex items-center gap-1">Tolérance
                <select aria-label="Tolérance d'arrivée" value={tolerance} onChange={(e) => onToleranceChange(Number(e.target.value))}
                  className="h-6 rounded-[4px] border border-slate-300 bg-white py-0 pl-1 pr-5 text-[12px] font-semibold text-slate-800">
                  {THRESHOLD_CHOICES.tolerance_min.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                </select>
              </label>
            )}
          </span>
        )}
      </div>
    </section>
  );
}
